/**
 * T129-012 — Closing Package Page
 *
 * Month picker → completeness checklist (each row deep-links) → Download zip.
 * Incompleteness warns but NEVER blocks download (D-9).
 * Zip assembled client-side with fflate.
 */
import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, AlertCircle, Download, Package } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Badge, Alert } from '@/components/ui'
import { Select } from '@/components/ui/Select'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday, formatMonthYear, type CalendarSystem } from '@/lib/date'
import { useClosingPackage } from '@/hooks/useClosingPackage'
import { assembleClosingPackage, triggerZipDownload } from '@/lib/closingPackage'
import { closingPackageCoverageWarning } from '@/lib/closingPackageCoverage'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useDeepLinkParams } from '@/hooks/useDeepLinkParams'

// ---------------------------------------------------------------------------
// Month picker helpers
// ---------------------------------------------------------------------------

/**
 * Build the list of the last 12 months as { value: 'YYYY-MM', label }.
 *
 * The label goes through formatMonthYear (lib/date.ts) so it honours the
 * active i18n locale AND the account's configured calendar era, matching the
 * Invoices/Payments billing-period pickers (billingPeriod.ts). Never call
 * toLocaleDateString('en-US', ...) here — that hardcoded every account,
 * every language, to English month names, and never omit the explicit
 * `calendar` option on formatMonthYear — CLDR defaults locale 'th' to the
 * Buddhist calendar, so a Gregorian-configured Thai account would silently
 * render 2569 (see formatMonthYear's own comment, from a real 2026-08-05 bug).
 */
function buildMonthOptions(
  currentMonth: string,
  locale: string,
  calendar: CalendarSystem,
): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = []
  const [year, month] = currentMonth.split('-').map(Number)

  for (let i = 0; i < 12; i++) {
    let m = month - i
    let y = year
    while (m <= 0) {
      m += 12
      y -= 1
    }
    const value = `${y}-${String(m).padStart(2, '0')}`
    const date = new Date(y, m - 1, 1)
    const label = formatMonthYear(date, { locale, calendar })
    options.push({ value, label })
  }

  return options
}

// ---------------------------------------------------------------------------
// Checklist row
// ---------------------------------------------------------------------------

interface ChecklistRowProps {
  label: string
  count: number | undefined
  to: string
  warn: boolean
  isLoading: boolean
  testId?: string
  message?: string | null
  actionLabel?: string
}

function ChecklistRow({ label, count, to, warn, isLoading, testId, message, actionLabel }: ChecklistRowProps) {
  const { t } = useTranslation('common')

  const statusOk = !warn
  const displayCount = isLoading ? '…' : (count ?? '-')

  return (
    <div
      className="flex items-center justify-between py-3 px-4 border-b border-base-200 last:border-b-0"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {statusOk ? (
          <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" aria-label={t('closing_package.status_ok')} />
        ) : (
          <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" aria-label={t('closing_package.status_warning')} />
        )}
        <Link to={to} className={`text-sm hover:text-primary transition-colors min-w-0 ${message ? '' : 'sm:truncate'}`}>
          {label}
          {message && <span className="block text-warning">{message}</span>}
        </Link>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
        {actionLabel ? <Link to={to} className="text-sm link link-primary">{actionLabel}</Link> : <span className="text-sm font-medium tabular-nums">{displayCount}</span>}
        {!statusOk && typeof count === 'number' && count > 0 && (
          <Badge variant="warning" size="sm">{t('closing_package.status_warning')}</Badge>
        )}
        {statusOk && typeof count === 'number' && (
          <Badge variant="success" size="sm">{t('closing_package.status_ok')}</Badge>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ClosingPackagePage() {
  const { t, i18n } = useTranslation('common')
  const { data: accountSettings } = useAccountSettings()
  const dateOptions = useLocalizationConfig()
  const { timezone, calendar } = dateOptions

  useDocumentTitle(t('closing_package.title'))

  const currentMonth = localToday(timezone).slice(0, 7)
  // EP-155: the accountant's dashboard links here with ?month=YYYY-MM (the
  // month it is closing, which may be the previous one).
  const deepLink = useDeepLinkParams(['month'] as const)
  const [selectedMonth, setSelectedMonth] = useState(
    deepLink.month && /^\d{4}-\d{2}$/.test(deepLink.month) && deepLink.month <= currentMonth ? deepLink.month : currentMonth,
  )
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const monthOptions = buildMonthOptions(currentMonth, i18n.language, calendar)

  const { data: manifest, isLoading } = useClosingPackage(selectedMonth)

  const accountingEnabled = accountSettings?.settings?.accounting_enabled ?? false
  const bankCoverageWarning = manifest ? closingPackageCoverageWarning(manifest, t, dateOptions) : null

  // Window boundaries for CSV filter params
  const windowFrom = manifest?.window_from ?? `${selectedMonth}-01`
  const windowTo = manifest?.window_to ?? `${selectedMonth}-31`

  // Check if there are any warnings
  const hasWarnings =
    !!bankCoverageWarning ||
    (manifest?.expenses_missing_receipt_count ?? 0) > 0 ||
    (manifest?.bank_rows_unmatched_count ?? 0) > 0 ||
    (manifest?.payments_pending_verification_count ?? 0) > 0 ||
    (manifest?.payments_unidentified_count ?? 0) > 0

  const handleDownload = async () => {
    if (!manifest) return

    setIsDownloading(true)
    setDownloadError(null)

    try {
      const result = await assembleClosingPackage({
        month: selectedMonth,
        windowFrom,
        windowTo,
        manifest,
        locale: i18n.language,
        dateOptions,
        accountingEnabled,
        // The assembler fetches and renders the full month CSVs.
        bankReconciliationCsv: undefined,
        journalEntriesCsv: undefined,
        // WHT template fetch — uses the document_templates API if available.
        // Silently omits WHT PDFs when template cannot be fetched.
        fetchWhtTemplate:
          manifest.wht_expenses.length > 0
            ? async () => {
                const { api } = await import('@/lib/api')
                const { data } = await api.get<{ data: { schema: unknown } }>(
                  '/api/document_templates',
                  { params: { template_type: 'wht_certificate' } },
                )
                // The schema field holds the pdfme Template JSON
                return data.data.schema as import('@pdfme/common').Template
              }
            : undefined,
      })

      triggerZipDownload(result.blob, result.filename)
    } catch (err) {
      console.error('[ClosingPackage] Assembly failed', err)
      setDownloadError(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[{ label: t('closing_package.title') }]}
      />

      <PageHeader
        title={t('closing_package.title')}
        primaryAction={{
          key: 'download',
          label: isDownloading ? t('closing_package.downloading') : t('closing_package.download_button'),
          icon: Download,
          onClick: handleDownload,
          loading: isDownloading,
          disabled: isDownloading || !manifest,
          testId: 'closing-package-download',
        }}
      />

      {/* Month picker */}
      <Card>
        <Select
          label={t('closing_package.month_picker_label')}
          value={selectedMonth}
          onChange={(e) => {
            setSelectedMonth(e.target.value)
            setDownloadError(null)
          }}
          options={monthOptions}
          data-testid="closing-package-month-picker"
        />
      </Card>

      {/* Download error */}
      {downloadError && (
        <Alert
          variant="error"
          data-testid="closing-package-error"
        >
          {downloadError}
        </Alert>
      )}

      {/* Warnings note (non-blocking) */}
      {hasWarnings && !isLoading && (
        <div
          className="flex items-start gap-2 p-3 bg-warning/10 rounded-lg border border-warning/20 text-sm"
          data-testid="closing-package-warnings-note"
        >
          <AlertCircle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{t('closing_package.warnings_note')}</span>
        </div>
      )}

      {/* Completeness checklist */}
      <Card title={t('closing_package.completeness_title')}>
        <div data-testid="closing-package-checklist">
          <ChecklistRow
            label={t('closing_package.checklist_invoices')}
            count={manifest?.invoices_count}
            to={`/invoices?from=${windowFrom}&to=${windowTo}`}
            warn={false}
            isLoading={isLoading}
            testId="checklist-row-invoices"
          />
          <ChecklistRow
            label={t('closing_package.checklist_payments')}
            count={manifest?.payments_count}
            to={`/payments?from=${windowFrom}&to=${windowTo}`}
            warn={false}
            isLoading={isLoading}
            testId="checklist-row-payments"
          />
          <ChecklistRow
            label={t('closing_package.checklist_expenses')}
            count={manifest?.expenses_count}
            to={`/expenses?from=${windowFrom}&to=${windowTo}`}
            warn={false}
            isLoading={isLoading}
            testId="checklist-row-expenses"
          />
          {/* Money out with no Expense behind it. Sits next to the expense rows
              rather than the bank row because the action it needs is "create an
              expense", not "reconcile a deposit". */}
          <ChecklistRow
            label={t('closing_package.checklist_pending_expenses')}
            count={manifest?.bank_rows_unmatched_debits_count}
            to="/bank-reconciliation"
            warn={(manifest?.bank_rows_unmatched_debits_count ?? 0) > 0}
            isLoading={isLoading}
            testId="checklist-row-pending-expenses"
          />
          <ChecklistRow
            label={t('closing_package.checklist_missing_receipts')}
            count={manifest?.expenses_missing_receipt_count}
            to={`/expenses?missing_receipt=true&from=${windowFrom}&to=${windowTo}`}
            warn={(manifest?.expenses_missing_receipt_count ?? 0) > 0}
            isLoading={isLoading}
            testId="checklist-row-missing-receipts"
          />
          <ChecklistRow
            label={t(bankCoverageWarning ? 'closing_package.checklist_bank_statement' : 'closing_package.checklist_unmatched_bank')}
            message={bankCoverageWarning}
            actionLabel={bankCoverageWarning ? t('actions.upload') : undefined}
            count={bankCoverageWarning ? undefined : manifest?.bank_rows_unmatched_count}
            to={bankCoverageWarning ? `/bank-reconciliation?month=${selectedMonth}&upload=1` : '/bank-reconciliation'}
            warn={!!bankCoverageWarning || (manifest?.bank_rows_unmatched_count ?? 0) > 0}
            isLoading={isLoading}
            testId="checklist-row-unmatched-bank"
          />
          <ChecklistRow
            label={t('closing_package.checklist_pending_verification')}
            count={manifest?.payments_pending_verification_count}
            to="/payments?verification_status=pending"
            warn={(manifest?.payments_pending_verification_count ?? 0) > 0}
            isLoading={isLoading}
            testId="checklist-row-pending-verification"
          />
          <ChecklistRow
            label={t('closing_package.checklist_unidentified_payments')}
            count={manifest?.payments_unidentified_count}
            to="/payments?unidentified=true"
            warn={(manifest?.payments_unidentified_count ?? 0) > 0}
            isLoading={isLoading}
            testId="checklist-row-unidentified-payments"
          />
        </div>
      </Card>

      {/* Empty state when no data loaded yet */}
      {!isLoading && !manifest && (
        <div className="flex flex-col items-center justify-center py-12 text-base-content/50">
          <Package className="w-12 h-12 mb-3" aria-hidden="true" />
          <p className="text-sm">{t('closing_package.title')}</p>
        </div>
      )}
    </div>
  )
}
